import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useState } from 'react';

// Define the isopen prop type for styled components
interface MobileMenuOverlayProps {
  $isopen: boolean;
}

const DeliveryBanner = styled.div<{ $isMenuOpen?: boolean }>`
  background-color: var(--primary-purple);
  color: white;
  padding: 0.4rem 0; /* thin banner */
  width: 100%;
  position: relative;
  z-index: 3000; /* sit above everything */
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  overflow: hidden;
  font-size: clamp(0.6rem, 1.4vw, 0.8rem);
  display: ${props => props.$isMenuOpen ? 'none' : 'block'};

  @media (max-width: 768px) {
    padding: 0.35rem 0;
  }
`;

const DeliveryBannerTrack = styled.div`
  display: inline-block;
  white-space: nowrap;
  animation: delivery-marquee 45s linear infinite;
  will-change: transform;

  @keyframes delivery-marquee {
    0% { transform: translate3d(100%, 0, 0); }
    100% { transform: translate3d(-100%, 0, 0); }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: none;
  }
`;


const TopBanner = styled.div<{ $isMenuOpen?: boolean }>`
  background-color: var(--primary-yellow);
  color: var(--primary-purple);
  padding: 0.75rem 0;
  width: 100%;
  display: ${props => props.$isMenuOpen ? 'none' : 'block'};

  a, svg {
    color: var(--primary-purple) !important;
    &:hover {
      color: #1a1116 !important;
    }
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0;
  }
`;

const TopBannerContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  justify-content: center;
  padding: 0 4rem;
  font-family: 'Texta', sans-serif;
  font-size: clamp(0.7rem, 1.6vw, 0.95rem);

  @media (max-width: 1400px) {
    padding: 0 2rem;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    text-align: center;
    font-size: clamp(0.7rem, 2vw, 0.9rem);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    justify-items: center;
    padding: 0 1rem;
    font-size: clamp(0.65rem, 2.4vw, 0.85rem);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  font-size: clamp(0.7rem, 2vw, 0.95rem);
  justify-self: start;

  svg {
    width: clamp(12px, 2vw, 18px);
    height: clamp(12px, 2vw, 18px);
    color: var(--primary-purple);
  }

  a {
    color: var(--primary-purple);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    &:hover {
      color: #1a1116;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    justify-self: center;
    gap: 0.4rem;
  }
`;

const DeliveryInfo = styled.div`
  text-align: center;
  justify-content: center;
  align-items: center;
  display: flex;
  color: var(--primary-purple);
  margin: 0 auto;
  font-weight: 500;
  padding: 0 1rem;
  font-family: Betrycia-Regular;
  font-size: 1.2rem;
  justify-self: center;
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-self: end;

  a {
    color: var(--primary-purple);
    display: flex;
    align-items: center;
    &:hover {
      color: #1a1116;
    }
  }

  svg {
    width: clamp(14px, 2.2vw, 20px);
    height: clamp(14px, 2.2vw, 20px);
    color: var(--primary-purple);
  }
  
  @media (max-width: 768px) {
    justify-self: center;
  }
`;

const Nav = styled.nav`
  background-color: var(--primary-purple);
  width: 100%;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 4rem;
  gap: 4rem;
  animation: fadeDown 0.6s ease both; /* load fade-down */
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 1400px) {
    padding: 1.5rem 2rem;
    gap: 2rem;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
  }
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
  img {
    height: 70px;
    width: auto;
    display: block;
  }

  @media (max-width: 1024px) {
    order: -1;
    margin: 0 auto 1rem;
    img {
      height: 60px;
    }
  }

  @media (max-width: 768px) {
    margin: 0 auto;
    order: 0;
    img {
      height: 60px; /* Enlarged logo for mobile */
    }
    
    &.desktop-only {
      display: none;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  justify-content: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &.left-links {
    justify-content: flex-end;
    padding-right: 2rem;
  }

  &.right-links {
    justify-content: flex-start;
    padding-left: 2rem;
  }

  @media (max-width: 1400px) {
    gap: 1.5rem;
    &.left-links { padding-right: 1rem; }
    &.right-links { padding-left: 1rem; }
  }

  @media (max-width: 1024px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
    text-align: center;
    padding: 0;
    border: none;
    
    &.left-links,
    &.right-links {
      padding: 0;
      justify-content: center;
    }
  }

  @media (max-width: 768px) {
    display: none; /* Hide navigation links on mobile */
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-family: 'Texta', sans-serif;
  font-weight: 500;
  font-size: 0.75rem;
  text-transform: uppercase;
  transition: color 0.25s ease;
  letter-spacing: 0.5px;
  white-space: nowrap;
  position: relative;
  
  &:hover {
    color: var(--primary-yellow);
  }

  /* underline slide-in */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -4px;
    width: 0%;
    height: 2px;
    background: var(--primary-yellow);
    transition: width 0.25s ease;
  }
  &:hover::after {
    width: 100%;
  }

  &.order-now {
    background-color: var(--primary-yellow);
    color: var(--primary-purple);
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(225, 180, 87, 0.3);
    transition: background-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
    will-change: transform;
    
    &:hover {
      background-color: #d1a34e;
      color: var(--primary-purple);
      transform: translateY(-3px);
      box-shadow: 0 8px 18px rgba(225, 180, 87, 0.4);
    }
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0;
    font-size: 0.7rem;
  }
`;

// External link styled like NavLink
const ExternalNavLink = styled.a`
  color: white;
  text-decoration: none;
  font-family: 'Texta', sans-serif;
  font-weight: 500;
  font-size: 0.75rem;
  text-transform: uppercase;
  transition: color 0.25s ease;
  letter-spacing: 0.5px;
  white-space: nowrap;
  position: relative;
  
  &:hover {
    color: var(--primary-yellow);
  }

  /* underline slide-in */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -4px;
    width: 0%;
    height: 2px;
    background: var(--primary-yellow);
    transition: width 0.25s ease;
  }
  &:hover::after {
    width: 100%;
  }

  &.order-now {
    background-color: var(--primary-yellow);
    color: var(--primary-purple);
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(225, 180, 87, 0.3);
    transition: background-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
    will-change: transform;
    
    &:hover {
      background-color: #d1a34e;
      color: var(--primary-purple);
      transform: translateY(-3px);
      box-shadow: 0 8px 18px rgba(225, 180, 87, 0.4);
    }
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0;
    font-size: 0.7rem;
  }
`;

// Mobile Menu Components
const MobileNavContainer = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
`;

const HamburgerIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 16px; /* Smaller toggle */
  padding: 0;
  
  span {
    display: block;
    height: 2px;
    width: 20px; /* Smaller toggle */
    background-color: white;
    transition: all 0.3s ease;
  }
`;

const OrderNowButton = styled(Link)`
  background-color: var(--primary-yellow);
  color: var(--primary-purple);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  text-decoration: none;
  font-family: 'Texta', sans-serif;
  font-size: 0.75rem;
  text-transform: uppercase;
  transition: all 0.2s;
  
  &:hover {
    background-color: #d1a34e;
    color: var(--primary-purple);
  }
`;

// External button styled like OrderNowButton
const ExternalOrderButton = styled.a`
  background-color: var(--primary-yellow);
  color: var(--primary-purple);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  text-decoration: none;
  font-family: 'Texta', sans-serif;
  font-size: 0.75rem;
  text-transform: uppercase;
  transition: all 0.2s;
  display: inline-block;
  
  &:hover {
    background-color: #d1a34e;
    color: var(--primary-purple);
    text-decoration: none;
  }
`;

const MobileMenuOverlay = styled.div<{ $isopen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: ${({ $isopen }) => $isopen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease-in-out;
`;

const MobileMenuItems = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 0 20px;
`;

const MobileMenuItem = styled(Link)`
  color: var(--primary-purple);
  text-decoration: none;
  font-family: 'Texta', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  text-transform: uppercase;
  transition: all 0.2s;
  text-align: center;
  width: 100%;
  padding: 8px 0;
  
  &:hover, &:active {
    color: var(--primary-purple);
    background-color: var(--primary-yellow);
    width: 100%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--primary-purple);
  font-size: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);
    
    // Toggle body scroll lock when menu opens/closes
    if (newMenuState) {
      document.body.style.overflow = 'hidden'; // Prevent scrolling
    } else {
      document.body.style.overflow = ''; // Re-enable scrolling
    }
  };

  return (
    <header>
      <DeliveryBanner $isMenuOpen={isMenuOpen} role="status" aria-live="polite">
        <DeliveryBannerTrack>
          Now Delivering to Gulberg, DHA, Model Town, Liberty Market & Surrounding Areas    |    Free Delivery on Orders Above Rs. 1500!
        </DeliveryBannerTrack>
      </DeliveryBanner>
      <TopBanner $isMenuOpen={isMenuOpen}>
        <TopBannerContent>
          <ContactInfo>
            <a href="tel:+923708025503">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 512 512">
                  <path fill="currentColor" d="M497.39 361.8l-112-48a24 24 0 0 0-28 6.9l-49.6 60.6A370.66 370.66 0 0 1 130.6 204.11l60.6-49.6a23.94 23.94 0 0 0 6.9-28l-48-112A24.16 24.16 0 0 0 122.6.61l-104 24A24 24 0 0 0 0 48c0 256.5 207.9 464 464 464a24 24 0 0 0 23.4-18.6l24-104a24.29 24.29 0 0 0-14.01-27.6z"/>
               </svg>
             <span>+92 370 8025503</span>
           </a>
            <a href="mailto:fancyandflour@gmail.com">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="16" rx="2" ry="2"></rect>
                <polyline points="3 6 12 13 21 6"></polyline>
              </svg>
              <span>fancyandflour@gmail.com</span>
            </a>
          </ContactInfo>
          <SocialLinks>
            <a href="https://www.facebook.com/share/1KNfQXMfev/" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </a>
            <a href="https://www.instagram.com/flour.andfancy.pk?igsh=MW44ZnhzNGQ4NmV4dg==" target="_blank" rel="noopener noreferrer" style={{ color: 'purple', textDecoration: 'none' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 448 512">
                  <path fill="currentColor" d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
              </svg>
            </a>
            <a href="https://wa.me/923708025503" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2a10 10 0 0 0-9.14 14.28L2 22l5.86-1.54A10 10 0 1 0 12 2zm5.2 14.57c-.23.65-1.36 1.24-1.88 1.27-.48.03-1.07.06-1.72-.11-.4-.1-.91-.29-1.57-.57-2.75-1.19-4.54-3.95-4.68-4.13-.14-.18-1.12-1.49-1.12-2.85s.71-2.02.96-2.29c.25-.27.55-.34.73-.34.18 0 .36.01.52.01.17 0 .39-.06.61.46.23.54.78 1.87.85 2.01.07.14.12.3.02.48-.1.18-.16.29-.32.45-.16.16-.33.38-.47.51-.15.15-.3.31-.13.61.18.3.79 1.3 1.7 2.12 1.17 1.04 2.15 1.36 2.46 1.51.31.15.5.13.69-.08.19-.21.81-.94 1.02-1.27.21-.33.44-.28.73-.17.29.11 1.84.86 2.16 1.01.32.15.54.23.62.35.08.12.08.68-.14 1.33z"/>
              </svg>
            </a>
          </SocialLinks>
        </TopBannerContent>
      </TopBanner>
      <Nav>
        <NavContainer>
          {/* Desktop Navigation */}
          <NavLinks className="left-links">
            <NavLink to="/">HOME</NavLink>
            <NavLink to="/category/banana-bread">BANANA BREAD</NavLink>
            <NavLink to="/category/pastries">PASTRIES</NavLink>
            <NavLink to="/category/donuts">DONUTS</NavLink>
            <NavLink to="/category/puff-pastries">PUFF PASTRIES</NavLink>
            <NavLink to="/category/sundaes">SUNDAES</NavLink>
          </NavLinks>

          {/* Center Logo - Desktop Only */}
          <Logo to="/" className="desktop-only">
            <img src="/FlourAndFancy_logo.PNG" alt="Flour & Fancy" />
          </Logo>

          {/* Right Menu - Desktop Only */}
          <NavLinks className="right-links">
            <NavLink to="/category/cakes">CAKES</NavLink>
            <NavLink to="/category/cupcakes">CUPCAKES</NavLink>
            <NavLink to="/category/fondant">FONDANT</NavLink>
            <NavLink to="/category/brownies">BROWNIES</NavLink>
            <NavLink to="/category/cookies">COOKIES</NavLink>
            <ExternalNavLink href="https://wa.me/923708025503" target="_blank" rel="noopener noreferrer" className="order-now">ORDER NOW</ExternalNavLink>
          </NavLinks>
          
          {/* Mobile Navigation - Hamburger left, Logo center, Order Now on right */}
          <MobileNavContainer style={{ width: '100%', justifyContent: 'space-between' }}>
            <HamburgerIcon onClick={toggleMenu}>
              <span></span>
              <span></span>
              <span></span>
            </HamburgerIcon>
            
            {/* Center Logo for Mobile */}
            <Logo to="/" style={{ margin: '0', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
              <img src="/FlourAndFancy_logo.PNG" alt="Flour & Fancy" />
            </Logo>
            
            <ExternalOrderButton href="https://wa.me/923708025503" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', padding: '0.4rem 0.6rem' }}>ORDER NOW</ExternalOrderButton>
          </MobileNavContainer>
        </NavContainer>
      </Nav>
      
      {/* Mobile Menu Overlay */}
      <MobileMenuOverlay $isopen={isMenuOpen}>
        <CloseButton onClick={toggleMenu}>
          ✕
        </CloseButton>
        <MobileMenuItems>
          <MobileMenuItem to="/" onClick={toggleMenu}>HOME</MobileMenuItem>
          <MobileMenuItem to="/category/banana-bread" onClick={toggleMenu}>BANANA BREAD</MobileMenuItem>
          <MobileMenuItem to="/category/pastries" onClick={toggleMenu}>PASTRIES</MobileMenuItem>
          <MobileMenuItem to="/category/donuts" onClick={toggleMenu}>DONUTS</MobileMenuItem>
          <MobileMenuItem to="/category/puff-pastries" onClick={toggleMenu}>PUFF PASTRIES</MobileMenuItem>
          <MobileMenuItem to="/category/sundaes" onClick={toggleMenu}>SUNDAES</MobileMenuItem>
          <MobileMenuItem to="/category/cakes" onClick={toggleMenu}>CAKES</MobileMenuItem>
          <MobileMenuItem to="/category/cupcakes" onClick={toggleMenu}>CUPCAKES</MobileMenuItem>
          <MobileMenuItem to="/category/fondant" onClick={toggleMenu}>FONDANT</MobileMenuItem>
          <MobileMenuItem to="/category/brownies" onClick={toggleMenu}>BROWNIES</MobileMenuItem>
          <MobileMenuItem to="/category/cookies" onClick={toggleMenu}>COOKIES</MobileMenuItem>
        </MobileMenuItems>
      </MobileMenuOverlay>
    </header>
  );
};

export default Navbar;